import {
  CaretDownIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import {
  ChangeEvent,
  KeyboardEvent,
  ComponentProps,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Day,
  DayButton,
  DayPicker,
  DropdownProps,
  getDefaultClassNames,
  useDayPicker,
} from 'react-day-picker';

import { cn } from '../cn';

/**
 * shadcn's Calendar recipe (https://ui.shadcn.com/docs/components/calendar)
 * over react-day-picker, styled to match the Metronic theme: a 328px panel
 * of 40px cells (px rather than rem: Metronic's root font is 13px), round 39px days, a filled grey "today", brand-600 selected
 * days and a brand-50 band behind ranges, six rows per month.
 *
 * The colours are that stylesheet's semantic tokens resolved to runtime
 * variables — the Bootstrap gray ramp (swapped per theme stylesheet) and
 * the brand ramp — with the dark-mode picks it made via `isDarkMode()`
 * expressed as `dark:` variants.
 *
 * Behaviour the form fields rely on:
 * - The root carries `data-slot="calendar"`, and every day cell keeps
 *   react-day-picker's own `data-day`/`data-disabled`/`data-selected`
 *   attributes — the date-picker story driver reads those. Unlike shadcn's
 *   recipe, the day *button* deliberately does not get a `data-day` of its
 *   own (shadcn sets a locale string there, which would shadow the cell's
 *   ISO date for any `[data-day]` lookup).
 * - Default class names (`rdp-*`) are kept alongside the utilities so the
 *   navigation buttons and range states stay addressable.
 * - `captionLayout="dropdown"` swaps the caption for month and year
 *   dropdowns (react-day-picker's custom `Dropdown` component, see
 *   https://daypicker.dev/guides/custom-components) — for dates far from
 *   today, such as a birth date. Without `startMonth`/`endMonth` the years
 *   run from 100 years ago to this year.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  components,
  formatters,
  ...props
}: ComponentProps<typeof DayPicker>) {
  const defaults = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      className={cn(
        'px-[24px] py-[20px] text-sm [--cell-size:40px]',
        // The band is the faint brand step in both themes (50 / 900), so the
        // day text on it stays the body colour, and only the edges are vivid.
        '[--calendar-band:var(--bg-brand-subtle)]',
        textSecondary,
        className,
      )}
      classNames={{
        root: cn('w-fit', defaults.root),
        months: cn(
          'relative flex flex-col gap-[24px] md:flex-row',
          defaults.months,
        ),
        // Positioned for the caption dropdowns' option grid.
        month: cn('relative flex flex-col', defaults.month),
        // Stacked over the caption — the positioned month would otherwise
        // paint on top and swallow the arrows' clicks — but click-through
        // between the arrows, where the caption dropdowns sit.
        nav: cn(
          'pointer-events-none absolute inset-x-0 top-0 z-[1] flex h-[40px] items-center justify-between',
          defaults.nav,
        ),
        button_previous: cn(navButton, defaults.button_previous),
        button_next: cn(navButton, defaults.button_next),
        month_caption: cn(
          'mb-[12px] flex h-[40px] items-center justify-center',
          defaults.month_caption,
        ),
        dropdowns: cn(
          'flex items-center justify-center gap-[4px]',
          defaults.dropdowns,
        ),
        caption_label: cn(
          'text-base font-medium select-none',
          defaults.caption_label,
        ),
        month_grid: cn('border-collapse', defaults.month_grid),
        weekdays: cn('flex h-[40px] items-center', defaults.weekdays),
        weekday: cn(
          // Clipped as a last resort; weekdayFormat keeps names short.
          'w-(--cell-size) overflow-hidden text-center text-xs font-medium text-ellipsis whitespace-nowrap select-none',
          defaults.weekday,
        ),
        week: cn('mt-[4px] flex', defaults.week),
        day: cn(
          'relative size-(--cell-size) p-0 text-center select-none',
          defaults.day,
        ),
        // The band behind a range: full cells in the middle (rounded where a
        // row breaks it), half cells under the round start and end.
        range_middle: cn(
          'bg-(--calendar-band) first:rounded-s-full last:rounded-e-full',
          '[&>button]:font-medium [&>button]:text-primary',
          defaults.range_middle,
        ),
        range_start: cn(
          'bg-[linear-gradient(to_right,transparent_50%,var(--calendar-band)_50%)]',
          'last:bg-none [&.rdp-range_end]:bg-none',
          defaults.range_start,
        ),
        range_end: cn(
          'bg-[linear-gradient(to_left,transparent_50%,var(--calendar-band)_50%)]',
          'first:bg-none [&.rdp-range_start]:bg-none',
          defaults.range_end,
        ),
        today: cn(
          '[&>button]:bg-quaternary [&>button]:font-medium',
          defaults.today,
        ),
        outside: cn('text-quaternary', defaults.outside),
        disabled: cn(disabledText, defaults.disabled),
        // Days before startMonth/after endMonth: react-day-picker renders
        // nothing for them; CalendarDay below shows them as disabled.
        hidden: cn(disabledText, defaults.hidden),
        ...classNames,
      }}
      formatters={{
        formatWeekdayName: (date, options, dateLib) =>
          dateLib.format(date, weekdayFormat(dateLib, options), options),
        // "Jan", as in MonthPicker: full names don't fit the month grid.
        formatMonthDropdown: (month, dateLib) => dateLib.format(month, 'LLL'),
        ...formatters,
      }}
      components={{
        Root: ({ className, rootRef, ...rootProps }) => (
          <div
            data-slot="calendar"
            ref={rootRef}
            className={className}
            {...rootProps}
          />
        ),
        Chevron: ({ className, orientation }) =>
          orientation === 'left' ? (
            <CaretLeftIcon className={cn('size-4', className)} weight="bold" />
          ) : (
            <CaretRightIcon className={cn('size-4', className)} weight="bold" />
          ),
        Day: CalendarDay,
        DayButton: CalendarDayButton,
        MonthsDropdown: CalendarMonthsDropdown,
        YearsDropdown: CalendarYearsDropdown,
        ...components,
      }}
      {...props}
    />
  );
}

type DateLib = Parameters<
  NonNullable<
    ComponentProps<typeof DayPicker>['formatters']
  >['formatWeekdayName']
>[2];
type DateLibOptions = Parameters<
  NonNullable<
    ComponentProps<typeof DayPicker>['formatters']
  >['formatWeekdayName']
>[1];

// Longest weekday header, in characters, that fits a 40px column.
const MAX_WEEKDAY_CHARS = 4;
const weekdayFormats = new Map<unknown, string>();

/**
 * The weekday header format for a locale: "Mon"-style (`EEE`)
 * where that fits, else date-fns's short (`EEEEEE`) or
 * narrow (`EEEEE`) names. `EEE` is far too long in several of the app's
 * languages — Estonian "esmasp.", Latvian "ceturtd.", Finnish "torst." —
 * and overlapped in the 40px columns. The header's aria-label keeps the
 * full name either way. Worked out once per locale.
 */
function weekdayFormat(dateLib: DateLib, options: DateLibOptions): string {
  const key = options?.locale ?? 'default';
  const cached = weekdayFormats.get(key);
  if (cached) return cached;
  // Any Monday–Sunday run will do.
  const week = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i));
  const fits = (pattern: string) =>
    week.every(
      (day) =>
        [...dateLib.format(day, pattern, options)].length <= MAX_WEEKDAY_CHARS,
    );
  const pattern = ['EEE', 'EEEEEE'].find(fits) ?? 'EEEEE';
  weekdayFormats.set(key, pattern);
  return pattern;
}

// The pickers' body text: --text-secondary is gray-700, and the subdued
// gray-dark-300 in dark mode, as it was when it read --bs-gray-700 /
// dark:--bs-gray-600.
const textSecondary = 'text-secondary';
const disabledText = 'text-[var(--text-disabled)]';

const dayShape =
  'mx-auto flex size-[39px] items-center justify-center rounded-full leading-none';

const navButton = cn(
  'pointer-events-auto inline-flex size-[40px] items-center justify-center p-0 select-none',
  'hover:text-brand',
  'focus-visible:[outline:2px_solid_var(--focus-ring-color)]',
  // Hide the navigation arrow past minDate/maxDate rather than dimming it.
  'disabled:invisible aria-disabled:invisible',
);

/**
 * react-day-picker renders an empty cell for an outside day beyond the
 * navigable months (e.g. 31 Aug when `startMonth` is September); keep
 * the number here, inert — unless outside days are turned off altogether.
 */
function CalendarDay({
  day,
  modifiers,
  children,
  ...props
}: ComponentProps<typeof Day>) {
  const { dayPickerProps } = useDayPicker();
  const showInert =
    modifiers.hidden && day.outside && dayPickerProps.showOutsideDays;
  return (
    <td {...props}>
      {showInert ? (
        <span className={cn(dayShape, disabledText)}>{day.date.getDate()}</span>
      ) : (
        children
      )}
    </td>
  );
}

function CalendarDayButton({
  className,
  // Not forwarded: `day` is react-day-picker data, not a DOM attribute.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  day: _day,
  modifiers,
  ...props
}: ComponentProps<typeof DayButton>) {
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const edge =
    (modifiers.selected && !modifiers.range_middle) ||
    modifiers.range_start ||
    modifiers.range_end;

  return (
    <button
      ref={ref}
      type="button"
      data-edge={edge || undefined}
      className={cn(
        dayShape,
        'hover:bg-(--calendar-band) hover:font-medium hover:text-primary',
        'disabled:pointer-events-none',
        'focus-visible:relative focus-visible:z-10 focus-visible:[outline:2px_solid_var(--focus-ring-color)]',
        'data-[edge]:bg-brand-600 data-[edge]:font-medium data-[edge]:text-white',
        'dark:data-[edge]:bg-brand-400 dark:data-[edge]:text-[var(--brand-on-400)]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A month or year dropdown in the caption. Not a native `<select>`: its
 * option list is drawn by the browser, which inside the popover (and the
 * modal dialogs around it, with their focus traps and `pointer-events:
 * none` on <body>) closed again as soon as it opened. Instead the button
 * opens a grid of the options over the days, like MonthPicker's months;
 * a pick goes to react-day-picker's `onChange` as a select's would.
 */
function CalendarDropdown({
  options,
  value,
  onChange,
  disabled,
  className,
  columns,
  'aria-label': ariaLabel,
}: DropdownProps & { columns: number }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const selected = options?.find((option) => option.value === value);

  useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    const current = panel?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (panel && current) {
      // Centre the current option; scrollIntoView would scroll the popover
      // and the page as well.
      panel.scrollTop =
        current.offsetTop - (panel.clientHeight - current.offsetHeight) / 2;
      current.focus({ preventScroll: true });
    }
    // A press anywhere else closes the grid (another dropdown, a nav arrow).
    const closeOnOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutside, true);
    return () =>
      document.removeEventListener('pointerdown', closeOnOutside, true);
  }, [open]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  // DatePickerPopover leaves an Escape from the grid to us.
  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };

  const pick = (next: number) => {
    onChange?.({
      target: { value: String(next) },
    } as ChangeEvent<HTMLSelectElement>);
    close();
  };

  return (
    // Not positioned: the grid lays itself over the whole month (rdp-month).
    <span ref={rootRef} data-slot="calendar-dropdown">
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className={cn(
          'inline-flex h-[32px] items-center gap-[4px] rounded-md ps-[8px] pe-[6px]',
          'text-base font-medium whitespace-nowrap text-primary select-none',
          'hover:bg-tertiary',
          'aria-expanded:bg-tertiary',
          'focus-visible:[outline:2px_solid_var(--focus-ring-color)]',
          'disabled:pointer-events-none',
          className,
        )}
      >
        {selected?.label}
        <CaretDownIcon
          className={cn(
            'size-[14px] transition-transform',
            open && 'rotate-180',
          )}
          weight="bold"
        />
      </button>
      {open ? (
        <div
          ref={panelRef}
          role="group"
          aria-label={ariaLabel}
          data-slot="calendar-dropdown-panel"
          className="absolute inset-x-0 top-[52px] bottom-0 z-10 grid content-start gap-y-[8px] overflow-y-auto bg-[var(--card-bg)] py-[4px]"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {options?.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={isSelected}
                data-value={option.value}
                disabled={option.disabled}
                onClick={() => pick(option.value)}
                onKeyDown={closeOnEscape}
                className={cn(
                  'mx-auto flex h-[39px] w-full max-w-[80px] items-center justify-center rounded-full leading-none',
                  'hover:bg-(--calendar-band) hover:font-medium hover:text-primary',
                  'focus-visible:[outline:2px_solid_var(--focus-ring-color)]',
                  'disabled:pointer-events-none disabled:text-[var(--text-disabled)]',
                  isSelected &&
                    'bg-brand-600 font-medium text-white hover:bg-brand-600 hover:text-white dark:bg-brand-400 dark:text-[var(--brand-on-400)]',
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </span>
  );
}

const CalendarMonthsDropdown = (props: DropdownProps) => (
  <CalendarDropdown {...props} columns={3} />
);

const CalendarYearsDropdown = (props: DropdownProps) => (
  <CalendarDropdown {...props} columns={4} />
);
